// Structured content for in-app legal overlays.
// Source of truth: src/assets/legal/terms.md and privacy.md
// string[] entries render as bulleted lists; strings render as paragraphs.

export type Block = string | string[];

export interface LegalSection {
  id: string;
  title: string;
  defaultOpen?: boolean;
  alwaysOpen?: boolean; // pinned open, no toggle
  content: Block[];
}

export const TERMS_EFFECTIVE = '1 January 2026';
export const PRIVACY_EFFECTIVE = '1 January 2026';
export const PRIVACY_UPDATED = '1 May 2026';

export const TERMS_SECTIONS: LegalSection[] = [
  {
    id: 'overview',
    title: 'Overview',
    defaultOpen: true,
    content: [
      'These Terms & Conditions ("Terms") govern your access to and use of the services provided by Ceen.Live ("We", "Us", "Our"), including our website, mobile application, API, and all other platforms, features, and tools (collectively, the "Platform"). By accessing or using the Platform, you agree to be legally bound by these Terms.',
    ],
  },
  {
    id: 'acknowledgment',
    title: 'Acknowledgment',
    defaultOpen: false,
    content: [
      'Please read these Terms carefully before using the Services.',
      'By accessing or using the Platform, you acknowledge that you have read and agreed to the Terms and Privacy Policy. If you disagree with any part of these Terms, you may not access the Service.',
      'These Terms apply to all visitors, users, and others who access or use the Services. If you are under 18, you may only access or use the Services with the involvement, consent, and supervision of a parent or legal guardian.',
    ],
  },
  {
    id: 'definitions',
    title: 'Definitions',
    defaultOpen: false,
    content: [
      'Key terms used throughout these Terms:',
      [
        '"Affiliate" — any entity that controls, is controlled by, or is under common control with Ceen.Live.',
        '"Content" — any text, images, audio, video, reviews, ratings, map data, event information, or other materials made available via the Platform.',
        '"Event Organizer" — a third party listing an event on the Platform.',
        '"Personal Data" — any information that identifies or can reasonably be used to identify you, including name, email, location, and device identifiers.',
        '"Platform" — the Ceen.Live website, mobile application, API, and all features.',
        '"Sensitive Personal Data" — categories including payment information, government IDs, precise geolocation, and biometric identifiers.',
      ],
    ],
  },
  {
    id: 'scope',
    title: 'Scope of Services',
    defaultOpen: true,
    content: [
      'The Platform provides location-based functionalities and event discovery solutions, enabling users to explore, plan, and engage with locations, venues, and events.',
      'Services include:',
      [
        'Search for and view information about locations, businesses, venues, and events',
        'Access maps, navigation, and directions',
        'Discover and filter events across categories — concerts, sports, theater, cultural programs, community gatherings, and more',
        'View event details such as timing, pricing, and venue facilities',
        'Book and purchase tickets, passes, or reservations',
        'Access personalized recommendations based on your preferences and location',
        'Share reviews, ratings, and user-generated content',
        'Allow Event Organizers to list and manage their events on the Platform',
      ],
      'Services may be updated or modified at any time. The Company acts solely as an intermediary and technology facilitator — we do not own or operate most venues or events listed.',
    ],
  },
  {
    id: 'account',
    title: 'Account & Security',
    defaultOpen: true,
    content: [
      'Account registration is currently available exclusively via Google Sign-In. You agree to provide true, accurate, and complete information and to keep it up to date.',
      'You are solely responsible for maintaining the confidentiality of your login credentials. All activities that occur under your account are deemed to have been conducted by you.',
      'Notify us immediately at support@ceen.live of any unauthorized use of your account or security breach.',
      'We reserve the right to suspend, restrict, or terminate access to your account where unauthorized use, suspicious activity, or violation of these Terms is suspected.',
    ],
  },
  {
    id: 'payments',
    title: 'Payments, Booking & Refunds',
    defaultOpen: false,
    content: [
      'Payments may be made only through the methods displayed on the Platform at the time of booking. All amounts are payable in full and subject to applicable taxes.',
      'A booking is confirmed only upon successful receipt of payment and issuance of a confirmation notification. Mere initiation of payment does not constitute confirmation.',
      "All cancellations, refunds, rescheduling, or modifications are governed exclusively by the Event Organizer's policies. We act solely as an intermediary and facilitate refunds strictly in accordance with Organizer instructions.",
    ],
  },
  {
    id: 'data',
    title: 'Data Collection & Use',
    defaultOpen: false,
    content: [
      'The collection, use, and protection of your Personal Data is governed by our Privacy Policy at ceen.live/privacy, incorporated into these Terms by reference. By agreeing to these Terms, you also agree to the Privacy Policy.',
      'You may withdraw consent for certain data processing at any time by adjusting your device or account settings, or by contacting us. Withdrawal of consent may limit certain features of the Services.',
    ],
  },
  {
    id: 'ip',
    title: 'Content & Intellectual Property',
    defaultOpen: false,
    content: [
      'All rights, title, and interest in the Platform, software, tools, content, and materials are and shall remain the sole and exclusive property of Ceen.Live. Nothing in these Terms transfers any such rights to you.',
      'You retain all rights in and to the data you provide. If you submit content (reviews, feedback), you grant us a worldwide, royalty-free, perpetual license to use, modify, and distribute such content.',
      'All rights in event materials, descriptions, images, and logos provided by Event Organizers remain the property of the respective Event Organizer. We merely display such content as an intermediary.',
    ],
  },
  {
    id: 'conduct',
    title: 'Acceptable Use',
    defaultOpen: false,
    content: [
      'You agree not to:',
      [
        'Create a false identity or misrepresent your identity',
        'Scrape, copy, or extract data from the Services using automated means',
        'Override security features or bypass access controls',
        'Violate the intellectual property rights of others',
        'Upload malicious code, viruses, or other harmful software',
        'Harass, abuse, or discriminate against any person',
        'Reverse engineer, decompile, or disassemble the Services',
        'Engage in framing or mirroring of the Platform',
        'Interfere with or harm the Platform, its infrastructure, or other users',
      ],
    ],
  },
  {
    id: 'disclaimers',
    title: 'Disclaimers & Warranties',
    defaultOpen: false,
    content: [
      'To the fullest extent permitted by law, the Services are provided on an "as is" and "as available" basis, without warranties of any kind. We expressly disclaim all implied warranties including merchantability, fitness for a particular purpose, and uninterrupted availability.',
      'We do not warrant that the Services will meet your requirements, be uninterrupted or error-free, or that results obtained will be accurate or reliable.',
      'We act solely as an intermediary. We are not liable for the acts, omissions, quality, cancellations, or accuracy of content attributable to Event Organizers or third-party providers.',
      'Participation in live events may involve inherent risks. By attending events booked through the Platform, you voluntarily assume all such risks.',
    ],
  },
  {
    id: 'liability',
    title: 'Limitation of Liability',
    defaultOpen: false,
    content: [
      'To the fullest extent permitted by law, we shall not be liable for any indirect, incidental, special, consequential, or punitive damages.',
      'We shall not be responsible for failures caused by server errors, faulty internet connections, or technical malfunctions beyond our control.',
      'We are not liable for damages arising from use or inability to use the Services, reliance on Platform content, unauthorized access to your data, actions of Event Organizers or third parties, or cancellation or modification of events.',
      'No claim arising from use of the Platform may be brought more than six (6) months after the cause of action first arose.',
    ],
  },
  {
    id: 'termination',
    title: 'Termination & Suspension',
    defaultOpen: false,
    content: [
      'We may suspend, restrict, or terminate your account at our sole discretion for breach of these Terms, provision of false information, engagement in fraud, or if required by law.',
      'You may terminate your account at any time by ceasing use of the Platform. Termination does not affect obligations already incurred.',
      'Upon termination, your right to access the Services ceases immediately. Provisions relating to payments, indemnities, limitations of liability, and dispute resolution survive termination.',
    ],
  },
  {
    id: 'general',
    title: 'General Provisions',
    defaultOpen: false,
    content: [
      [
        'Severability — If any provision is held invalid, the remaining provisions remain in full force.',
        'Force Majeure — We are not liable for failure or delay due to events beyond our reasonable control, including natural disasters, internet outages, or government actions.',
        'Class Action Waiver — Claims may only be brought on an individual basis. You waive any right to a jury trial or class action proceeding.',
        'Notices — Required notices shall be delivered by email to legal@ceen.live.',
        'Modification — We reserve the right to modify these Terms at any time. Changes take effect when posted; continued use constitutes acceptance.',
        'Cookies — The Platform uses cookies and similar technologies. By using the Services, you consent to their use in accordance with our Privacy Policy.',
      ],
    ],
  },
  {
    id: 'appstores',
    title: 'App Store Terms',
    defaultOpen: false,
    content: [
      "The Platform may be available through the Apple App Store, Google Play Store, or other distribution channels. Your use through any App Store is subject exclusively to that App Store's terms and policies.",
      'We disclaim any responsibility for the availability, functionality, or policies of any App Store. Your contractual relationship with an App Store is independent of these Terms.',
    ],
  },
  {
    id: 'governing',
    title: 'Governing Law & Disputes',
    defaultOpen: false,
    content: [
      'These Terms are governed by and construed in accordance with the laws of Delaware.',
      'Any dispute will first be resolved through good-faith negotiations. If not resolved within 30 days, the matter shall be finally resolved by binding individual arbitration in Oakland, California, administered under the rules of the American Arbitration Association (AAA).',
      'All proceedings shall be conducted on an individual basis — not as a class, collective, or representative action.',
    ],
  },
  {
    id: 'contact',
    title: 'Contact Us',
    defaultOpen: true,
    alwaysOpen: true,
    content: [
      'For general inquiries: support@ceen.live',
      'By using the platform, you acknowledge that you have read, understood, and agree to these Terms.',
    ],
  },
];

export const PRIVACY_SECTIONS: LegalSection[] = [
  {
    id: 'tldr',
    title: 'TL;DR',
    defaultOpen: true,
    alwaysOpen: true,
    content: [
      'Ceen helps you discover and save events near you. To use the app, you sign in with Google, which gives us your basic profile info (name and email). If you choose to connect your calendar, we can save events there on your behalf — you can revoke this anytime in your Google settings.',
      "We store the events and lists you create in the app. We don't sell your data. You can ask us to delete your data at any time by contacting privacyplease@ceen.live.",
    ],
  },
  {
    id: 'intro',
    title: 'Introduction',
    defaultOpen: false,
    content: [
      'This Privacy Policy ("Policy") governs how Ceen Co. ("Company", "We", "Us") collects, uses, discloses, and protects your data when you access or use our website, mobile applications, and related services (collectively, the "Platform").',
      'By using the Platform, you agree to the terms of this Policy. This Policy describes the broadest potential use of your data, though we may make far less use in practice. You may withdraw consent at any time, though this may limit access to certain features.',
    ],
  },
  {
    id: 'collect',
    title: 'Information We Collect',
    defaultOpen: true,
    content: [
      'We collect information when you create or update an account, use our Services, search for or book events, enable third-party integrations, or contact us. This includes:',
      [
        'Google Account Data — When you sign in via Google, we receive your name, email address, and profile picture. We do not receive your Google password.',
        'Google Calendar Data (Optional) — If you connect your Google Calendar, we may read and write event data on your behalf. This can be revoked at any time in your Google account settings at myaccount.google.com.',
        'Event & Booking Data — Event registrations, ticket bookings, and transaction histories.',
        'Location Data — Limited location data tied to events you explore.',
        'Usage Data — Pages viewed, search queries, session duration, and in-app activities.',
        'User-Generated Content — Lists, event submissions, and other content you create on the Platform.',
      ],
    ],
  },
  {
    id: 'use',
    title: 'How We Use Your Data',
    defaultOpen: false,
    content: [
      'Your data is used to:',
      [
        'Operate, maintain, and improve the Platform and Services',
        'Send confirmations, technical notices, and responses to support requests',
        'Monitor usage and performance to improve features',
        'Detect fraud, unauthorized access, and misuse',
        'Facilitate integrations with third-party services (maps, payments, analytics)',
        'Comply with applicable laws and legal obligations',
      ],
      'We may share your data with service providers (payment processors, analytics platforms), business partners, and legal or regulatory authorities when required by law. We do not sell, trade, or monetize your personal data.',
    ],
  },
  {
    id: 'security',
    title: 'Security',
    defaultOpen: false,
    content: [
      'We implement technical, administrative, and organizational safeguards to protect your data, including access controls, secure Google-based authentication, and activity monitoring for suspicious access.',
      'While we employ industry-standard security measures, no system is entirely immune from breaches. We encourage you to use strong passwords on your Google account and enable two-factor authentication.',
    ],
  },
  {
    id: 'cookies',
    title: 'Cookies & Tracking',
    defaultOpen: false,
    content: [
      'We use cookies and similar technologies to enhance your experience, remember preferences, and analyze usage patterns. Types we use:',
      [
        'Essential Cookies — required for basic Platform operation and authentication',
        'Performance & Analytics — monitor usage patterns and improve functionality',
        'Functionality Cookies — remember user preferences and settings',
        'Advertising Cookies — deliver relevant ads and measure campaign effectiveness',
        'Third-Party Cookies — from services like maps and social logins, governed by their own policies',
      ],
      'You may adjust your browser or device settings to refuse or delete cookies. Disabling certain cookies may limit some Platform features.',
    ],
  },
  {
    id: 'rights',
    title: 'Your Rights',
    defaultOpen: true,
    content: [
      'You have the following rights regarding your personal data:',
      [
        'Access — request access to the data we hold about you',
        'Correction — request correction of inaccurate or outdated data',
        'Deletion — request deletion of your data, subject to legal retention obligations',
        'Restriction — request that we limit processing in certain circumstances',
        'Opt-Out — opt out of promotional communications or targeted advertising',
        'Withdraw Consent — withdraw consent at any time, including for Google Calendar access',
        'Google Calendar Revocation — revoke calendar access directly at myaccount.google.com',
      ],
      'Submit requests to privacyplease@ceen.live. We will respond within a reasonable time and may require identity verification.',
    ],
  },
  {
    id: 'breach',
    title: 'Data Breach & Notification',
    defaultOpen: false,
    content: [
      'In the event of a confirmed or suspected data breach, we will take commercially reasonable steps to notify affected users promptly, consistent with applicable law.',
      'Upon discovery of a breach, we will investigate its scope, notify users and authorities as required, implement containment measures, and cooperate with law enforcement.',
    ],
  },
  {
    id: 'thirdparty',
    title: 'Third-Party Links',
    defaultOpen: false,
    content: [
      'Our Platform includes links to third-party websites and services provided for your convenience. Third-party services are governed by their own terms and privacy policies — we are not responsible for their practices or the outcome of your use of those services.',
    ],
  },
  {
    id: 'international',
    title: 'International Transfers',
    defaultOpen: false,
    content: [
      'Your data may be transferred to, stored, and processed in countries other than your country of residence. We implement appropriate safeguards to ensure your data is protected in accordance with applicable laws.',
      'By using the Platform, you consent to such transfers.',
    ],
  },
  {
    id: 'general',
    title: 'General Provisions',
    defaultOpen: false,
    content: [
      [
        'Severability — If any provision is held invalid, the remaining provisions remain in full force.',
        'Notices — Privacy-related notices shall be delivered to privacyplease@ceen.live.',
        'Modification — We may modify this Policy at any time. Changes are posted with an updated effective date. Material changes will be notified via email or in-app notice.',
      ],
    ],
  },
  {
    id: 'governing',
    title: 'Governing Law & Disputes',
    defaultOpen: false,
    content: [
      'This Policy is governed by the laws of Delaware. Any dispute will first be resolved through good-faith negotiations. If not resolved within 30 days, the matter shall be resolved by binding individual arbitration in Oakland, California, under AAA rules.',
    ],
  },
  {
    id: 'contact',
    title: 'Contact Us',
    defaultOpen: true,
    alwaysOpen: true,
    content: [
      'For questions, concerns, or data requests: privacyplease@ceen.live',
      'By using the platform, you acknowledge that you have read, understood, and agree to this Policy.',
    ],
  },
];
